import { useState, useCallback } from "react";
import { useAuth } from "../../../contexts/auth/Context";
import { useToast } from "../../../contexts/toast/Context";
import { updateAdminPaymentMethodRequest } from "../../requests/admin/gym";
import type { GymPaymentMethod } from "../../../utils/zod/admin";
import type { CommissionSettings } from "../../../hooks/requests/paymentMethod";

type AnyPaymentMethod = {
  publicId: string;
  name: string;
  ticketConfig: {
    plan: { requires: boolean };
    food: { requires: boolean };
    merchandise: { requires: boolean };
  } | null;
  additionalSettings?: { commission?: CommissionSettings; logo_url?: string };
};

export interface PaymentMethodEditForm {
  name: string;
  ticketPlan: boolean;
  ticketFood: boolean;
  ticketMerchandise: boolean;
  hasCommission: boolean;
  commissionPercentage: string;
  commissionFixedFee: string;
  commissionBase: "TOTAL_GROSS" | "TOTAL_NET";
  commissionTaxType: "vat" | "none" | "custom";
  commissionTaxValue: string;
}

function initForm(pm: AnyPaymentMethod): PaymentMethodEditForm {
  const commission = pm.additionalSettings?.commission;
  return {
    name: pm.name,
    ticketPlan: pm.ticketConfig?.plan.requires ?? false,
    ticketFood: pm.ticketConfig?.food.requires ?? false,
    ticketMerchandise: pm.ticketConfig?.merchandise.requires ?? false,
    hasCommission: !!commission,
    commissionPercentage: commission ? String(commission.percentage) : "0",
    commissionFixedFee: commission ? String(commission.fixedFee) : "0",
    commissionBase: commission?.calculationBase ?? "TOTAL_GROSS",
    commissionTaxType: commission?.tax.type ?? "none",
    commissionTaxValue:
      commission?.tax.value !== undefined ? String(commission.tax.value) : "",
  };
}

export const usePaymentMethodEdit = (
  pm: AnyPaymentMethod,
  gymPublicId: string,
  onSuccess: (updated: GymPaymentMethod) => void,
) => {
  const { token } = useAuth();
  const { showToast } = useToast();

  const [form, setForm] = useState<PaymentMethodEditForm>(() => initForm(pm));
  const [isSaving, setIsSaving] = useState(false);

  const resetForm = useCallback(() => {
    setForm(initForm(pm));
  }, [pm]);

  const updateField = useCallback(
    <K extends keyof PaymentMethodEditForm>(
      field: K,
      value: PaymentMethodEditForm[K],
    ) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const save = useCallback(async () => {
    if (!token) return;
    setIsSaving(true);
    try {
      const commission: CommissionSettings | undefined = form.hasCommission
        ? {
            percentage: parseFloat(form.commissionPercentage) || 0,
            fixedFee: parseFloat(form.commissionFixedFee) || 0,
            calculationBase: form.commissionBase,
            tax: {
              type: form.commissionTaxType,
              ...(form.commissionTaxType !== "none" && form.commissionTaxValue
                ? { value: parseFloat(form.commissionTaxValue) }
                : {}),
            },
          }
        : undefined;

      const updated = await updateAdminPaymentMethodRequest(
        gymPublicId,
        pm.publicId,
        {
          name: form.name.trim(),
          ticketConfig: {
            plan: { requires: form.ticketPlan },
            food: { requires: form.ticketFood },
            merchandise: { requires: form.ticketMerchandise },
          },
          additionalSettings: form.hasCommission ? { commission } : null,
        },
        token,
      );
      onSuccess(updated);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Error al actualizar el método de pago";
      showToast(msg, "error");
    } finally {
      setIsSaving(false);
    }
  }, [token, form, gymPublicId, pm.publicId, onSuccess, showToast]);

  return { form, updateField, resetForm, save, isSaving };
};
